import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { IonPage, isPlatform, useIonViewDidEnter, useIonViewDidLeave, useIonViewWillEnter } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { extractCurrentUrlUtmParams, useAnalytics } from '../../shared';
import { PrivateRoutes, PublicRoutes } from '../../routes/navigation';
import { RouteComponentProps } from 'react-router';
import { isLinkUserExistSelector, isTokenInitSelector } from '../../redux/auth/selector';
import { useDispatch, useSelector } from 'react-redux';
import {
  setIsTokenInit,
  setNewOnBoardingAnswers,
  setNewOnboardingData,
  setOnBoardingUserId,
  setSignUpEmail,
  setIsWebToWebAuth,
} from '../../redux/auth/reducer';
import { getUserById } from '../../api/user';
import { Browser } from '@capacitor/browser';
import { loadUserThunk } from 'src/redux/user/thunk';
import { DefaultAuthWidget, WebToWebAuthWidget } from 'src/widgets/Auth';
import { safeParse } from 'src/utils';
import { useAuthFlow } from 'src/shared';
import useToast from 'src/context/ToastContext/ToastContext';
import { clearSavedFilters } from 'src/shared/utils/filters';
import LoadingContainerFeature from 'src/features/Auth/LoadingContainerFeature';

const Auth: FC<RouteComponentProps> = ({ history }) => {
  const { i18n } = useTranslation('auth');
  const { showToast } = useToast();

  const {
    triggerSignInWithGoogleFlow,
    triggerSignInWithAppleFlow,
    triggerSignInWithCustomTokenFlow,
    triggerLinkWithGoogleFlow,
    triggerLinkWithAppleFlow,
    triggerSignOutFlow,
    authUser,
  } = useAuthFlow();
  const dispatch = useDispatch();

  const [authType, setAuthType] = useState<'apple' | 'google' | null>(null);
  const [isTokenLoading, setIsTokenLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOnboardingDataLoading, setIsOnboardingDataLoading] = useState(false);
  const isLinkUserExist = useSelector(isLinkUserExistSelector);

  const isTokenInit = useSelector(isTokenInitSelector);
  const getParams = new URLSearchParams(window.location.search);
  const isOnboardingSkipped = useMemo(() => {
    return getParams.get('skip_onboarding') === 'true';
  }, []);
  const isWeb = isPlatform('mobileweb') || isPlatform('desktop');
  const token = getParams.get('token');
  const isWebToWebAuth = getParams.get('isWebToWebAuth');
  const webToWebAuthStorage = localStorage.getItem('webToWebAuth');

  const { trackPredefinedEvent } = useAnalytics();

  const handleTokenAuth = useCallback(async () => {
    if (isTokenLoading) {
      return;
    }

    if (token) {
      localStorage.setItem('onboardingToken', token);
    }

    // if user logged in and came from onboarding, need to log out
    if (token && authUser && authUser?.email) {
      clearSavedFilters(i18n.language, authUser.uid);
      await triggerSignOutFlow();
    }

    if (authUser && !authUser?.isAnonymous && authUser?.email && !token) {
      history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
      localStorage.removeItem('webToWebAuth');
      return;
    }

    if (token && !isTokenInit) {
      try {
        setIsTokenLoading(true);
        authUser && (await triggerSignOutFlow());
        const anonymousAuthData = await triggerSignInWithCustomTokenFlow(token);
        if (anonymousAuthData?.user) {
          const anonymousUserData = await getUserById(anonymousAuthData.user.uid);
          // user from registration flows need to be redirected to fill personal info
          if (
            anonymousUserData?.email &&
            !anonymousUserData?.dob &&
            anonymousAuthData.user.providerData.find((provider: any) => provider.providerId === 'password')
          ) {
            dispatch(setSignUpEmail(anonymousUserData.email));
            history.push(PublicRoutes.NAME_SIGN_UP);
            return;
          }
          if (anonymousUserData?.onBoardingAnswers) {
            dispatch(setNewOnBoardingAnswers(anonymousUserData.onBoardingAnswers));
          }
          if (anonymousUserData?.onboarding_key) {
            dispatch(
              setNewOnboardingData({
                id: anonymousUserData.onboarding_key,
                name: anonymousUserData?.onboarding_name ?? '',
                type: anonymousUserData?.onboarding_type ?? '',
                experiment: anonymousUserData?.onboarding_experiment_name ?? null,
              })
            );
          }
          dispatch(setOnBoardingUserId(anonymousAuthData.user.uid));
          if (anonymousUserData?.email) {
            await dispatch(loadUserThunk(anonymousAuthData?.user.uid)).unwrap();
            history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
            localStorage.removeItem('webToWebAuth');
          }
        }
      } catch (e) {
        console.error('[Auth] handleTokenAuth', e);
      } finally {
        setIsTokenLoading(false);
        dispatch(setIsTokenInit(true));
      }
    }
  }, [authUser, isTokenInit, isTokenLoading, token, i18n.language]);

  useIonViewWillEnter(() => {
    if (authUser && !authUser?.isAnonymous && authUser?.email && !token) {
      history.push(PrivateRoutes.PSYCHOLOGIST_LIST);

      localStorage.removeItem('webToWebAuth');
    }
  }, [authUser, token]);

  const showWebToWebAuth = useMemo(() => {
    const parsedIsWebToWebAuth = safeParse<boolean>(isWebToWebAuth ?? '');

    if (isWeb && parsedIsWebToWebAuth === false) {
      return false;
    }

    if (isWeb && (parsedIsWebToWebAuth || webToWebAuthStorage)) {
      return true;
    }
  }, [isWeb, isWebToWebAuth, webToWebAuthStorage]);

  useEffect(() => {
    if (showWebToWebAuth) {
      dispatch(setIsWebToWebAuth(true));

      if (!webToWebAuthStorage) {
        localStorage.setItem('webToWebAuth', isWebToWebAuth ?? '');
      }
    } else {
      dispatch(setIsWebToWebAuth(false));
      localStorage.removeItem('webToWebAuth');
    }
  }, [showWebToWebAuth]);

  useIonViewDidEnter(() => {
    if (!authUser?.isAnonymous && authUser?.email && !token) {
      return;
    }

    trackPredefinedEvent('LOGIN_GET_STARTED_VIEWED');
  }, [authUser, token]);

  useIonViewDidEnter(() => {
    const utmParams = extractCurrentUrlUtmParams();

    if (token) {
      handleTokenAuth();
    }

    if (Object.keys(utmParams).length > 0) {
      localStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [token]);

  useIonViewDidLeave(() => {
    setIsTokenLoading(false);
  });

  const handleAppleSignIn = useCallback(async () => {
    setAuthType('apple');
    try {
      // Show loader only on native platforms, because on web we use redirect method
      setIsLoading(isPlatform('hybrid'));
      trackPredefinedEvent('LOGIN_APPLE_BUTTON_CLICKED');

      if (authUser && !authUser?.email) {
        await triggerLinkWithAppleFlow();
      } else {
        await triggerSignInWithAppleFlow();
      }
    } catch (error: any) {
      showToast({ error });
    } finally {
      setIsLoading(false);
    }
  }, [triggerSignInWithAppleFlow, triggerLinkWithAppleFlow, authUser]);

  const handleGoogleSignIn = useCallback(async () => {
    setAuthType('google');
    try {
      // Show loader only on native platforms, because on web we use redirect method
      setIsLoading(isPlatform('hybrid'));
      trackPredefinedEvent('LOGIN_GOOGLE_BUTTON_CLICKED');

      if (authUser && !authUser?.email) {
        await triggerLinkWithGoogleFlow();
      } else {
        await triggerSignInWithGoogleFlow();
      }
    } catch (error: any) {
      showToast({ error });
    } finally {
      setIsLoading(false);
    }
  }, [triggerSignInWithGoogleFlow, triggerLinkWithGoogleFlow, authUser]);

  const handleSignIn = useCallback(async () => {
    try {
      setIsLoading(true);
      if (authType === 'apple') {
        await triggerSignInWithAppleFlow();
      } else {
        await triggerSignInWithGoogleFlow();
      }
    } finally {
      setIsLoading(false);
    }
  }, [authType, triggerSignInWithAppleFlow, triggerSignInWithGoogleFlow]);

  const handleLinkClick = useCallback(async (url: string) => {
    await Browser.open({
      url,
      presentationStyle: 'fullscreen',
      windowName: '_blank',
    });
  }, []);

  return (
    <IonPage>
      {isTokenLoading || isOnboardingDataLoading ? (
        <LoadingContainerFeature isOnboardingSkipped={isOnboardingSkipped} showWebToWebAuth={showWebToWebAuth} />
      ) : null}

      {showWebToWebAuth ? (
        <WebToWebAuthWidget
          isLoading={isLoading}
          isLinkUserExist={isLinkUserExist}
          authType={authType}
          handleSignIn={handleSignIn}
          handleGoogleSignIn={handleGoogleSignIn}
          handleAppleSignIn={handleAppleSignIn}
          handleLinkClick={handleLinkClick}
          setIsOnboardingDataLoading={setIsOnboardingDataLoading}
        />
      ) : (
        <DefaultAuthWidget
          isLoading={isLoading}
          isLinkUserExist={isLinkUserExist}
          authType={authType}
          handleSignIn={handleSignIn}
          handleGoogleSignIn={handleGoogleSignIn}
          handleAppleSignIn={handleAppleSignIn}
          handleLinkClick={handleLinkClick}
          setIsOnboardingDataLoading={setIsOnboardingDataLoading}
        />
      )}
    </IonPage>
  );
};

export default Auth;
